/**
 * Identité du terminal envoyée par le battement (tenant_devices).
 *
 * Lue à CHAQUE battement : les drapeaux natifs (__VISIONPOS_DEVICE_MODEL…) sont injectés
 * par l'APK quand la page a fini de charger, souvent après le montage de React. Lue une
 * seule fois au montage, 12 des 19 caisses natives remontaient « web » comme modèle
 * (relevé prod 2026-11-02).
 *
 * Les ponts JS natifs, eux, existent dès le premier script : getCapabilities()
 * (APK ≥ 1.5.9.16) donne modèle, fabricant et niveau d'API Android.
 */

type NativeWindow = {
  VisionPOSRemoteOps?: { getCapabilities?: () => string };
  VisionPOSUpdater?: { getCurrentVersion?: () => string; getCurrentVersionCode?: () => number };
  __VISIONPOS_ANDROID?: boolean;
  __VISIONPOS_DEVICE_MODEL?: string;
  __VISIONPOS_DEVICE_MANUFACTURER?: string;
  __VISIONPOS_ANDROID_SDK?: number;
  __VISIONPOS_DEVICE_NAME?: string;
};

export type DeviceContext = {
  isNative: boolean;
  model: string;
  manufacturer: string | null;
  androidSdk: number | null;
  osVersion: string | null;
  deviceName: string | null;
  appVersion: string;
  versionCode: number;
  remoteOps: boolean;
  root: boolean | null;
};

type Capabilities = {
  model?: string;
  manufacturer?: string;
  android_sdk?: number;
  reboot?: boolean;
};

function readCapabilities(w: NativeWindow): Capabilities | null {
  try {
    const raw = w.VisionPOSRemoteOps?.getCapabilities?.();
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Capabilities) : null;
  } catch {
    return null;
  }
}

function readNativeVersion(w: NativeWindow): { name: string; code: number } | null {
  try {
    const name = w.VisionPOSUpdater?.getCurrentVersion?.();
    const code = Number(w.VisionPOSUpdater?.getCurrentVersionCode?.());
    if (name && Number.isFinite(code) && code > 0) return { name: String(name), code };
  } catch {
    /* pont absent */
  }
  return null;
}

export function detectDeviceContext(
  w: NativeWindow,
  userAgent: string,
  platform: string,
): DeviceContext {
  const caps = readCapabilities(w);
  const version = readNativeVersion(w);
  const isNative = version !== null || w.__VISIONPOS_ANDROID === true;
  const androidMatch = userAgent.match(/Android\s([\d.]+)/);
  const sdk = typeof caps?.android_sdk === 'number' ? caps.android_sdk : w.__VISIONPOS_ANDROID_SDK;
  return {
    isNative,
    model: caps?.model || w.__VISIONPOS_DEVICE_MODEL || 'web',
    manufacturer: caps?.manufacturer || w.__VISIONPOS_DEVICE_MANUFACTURER || null,
    androidSdk: typeof sdk === 'number' ? sdk : null,
    osVersion: androidMatch ? 'Android ' + androidMatch[1] : platform || null,
    deviceName: w.__VISIONPOS_DEVICE_NAME || null,
    // Navigateur (PC, téléphone) : « web », plus la version de secours 1.5.9.15 qui
    // faisait croire à 5 caisses en retard de 20 versions.
    appVersion: version?.name ?? 'web',
    versionCode: version?.code ?? 0,
    remoteOps: typeof w.VisionPOSRemoteOps?.getCapabilities === 'function',
    root: typeof caps?.reboot === 'boolean' ? caps.reboot : null,
  };
}
